import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Spin, Tooltip, message } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StandardLayout from '../../../../components/Layout/StandardLayout';
import AudienceBuilder from '../../../shared/AudienceBuilder';
import AudiencePreviewModal from '../../../shared/AudienceBuilder/AudiencePreviewModal';
import MassMessageForm from '../../../shared/MassMessageForm';
import { MASS_MESSAGE_TEMPLATE_BUTTONS } from '../../../shared/MassMessageForm/constants';
import { useAudienceCount } from '../../../../hooks/query/useAudiences';
import { useMassMessage, useUpdateMassMessage } from '../../../../hooks/query/useMassMessages';
import './styles.scss';

const EMPTY_CONFIG = { version: 1, operator: 'AND', tags: { included: [] } };

const EditMassMessage2 = () => {
  const { messageId } = useParams();
  const history = useHistory();
  const formRef = useRef();
  const updateMutation = useUpdateMassMessage();
  const audienceCountMutation = useAudienceCount();

  const { data, isLoading } = useMassMessage({ massMessageId: messageId });
  const msg = data?.data || data || {};

  // Audience
  const [audienceConfig, setAudienceConfig] = useState(EMPTY_CONFIG);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Initialize audience config from fetched message
  useEffect(() => {
    if (!initialized && msg.audience?.configuration) {
      const config = msg.audience.configuration;
      setAudienceConfig(config);
      setInitialized(true);

      const hasFilters =
        config?.tags?.included?.length > 0 ||
        config?.tags?.excluded?.length > 0 ||
        config?.stores?.included?.length > 0 ||
        config?.contactMethods?.length > 0 ||
        !!config?.lastContacted;

      if (hasFilters) {
        audienceCountMutation.mutate(config);
      }
    }
  }, [msg, initialized]); // eslint-disable-line react-hooks/exhaustive-deps

  const audienceCount = audienceCountMutation.data?.total ?? audienceCountMutation.data?.count ?? 0;
  const isCountLoading = audienceCountMutation.isLoading;

  const handleAudienceChange = useCallback(
    newConfig => {
      setAudienceConfig(newConfig);
      const hasFilters =
        newConfig?.tags?.included?.length > 0 ||
        newConfig?.tags?.excluded?.length > 0 ||
        newConfig?.stores?.included?.length > 0 ||
        newConfig?.contactMethods?.length > 0 ||
        !!newConfig?.lastContacted;

      if (hasFilters) {
        audienceCountMutation.mutate(newConfig);
      }
    },
    [audienceCountMutation],
  );

  const hasAudienceFilters = useCallback(
    () =>
      audienceConfig?.tags?.included?.length > 0 ||
      audienceConfig?.tags?.excluded?.length > 0 ||
      audienceConfig?.stores?.included?.length > 0 ||
      audienceConfig?.contactMethods?.length > 0 ||
      !!audienceConfig?.lastContacted,
    [audienceConfig],
  );

  const handleFormSubmit = async formData => {
    if (!hasAudienceFilters()) {
      message.error('Please add at least one audience filter');
      return;
    }

    const payload = {
      ...formData,
      audience: { configuration: audienceConfig },
    };

    try {
      await updateMutation.mutateAsync({ massMessageId: messageId, data: payload });
      message.success('Message updated successfully');
      history.push('/Messaging/mass-messaging');
    } catch (error) {
      console.error('Error updating mass message:', error);
      message.error('Failed to update message. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <StandardLayout
        title="Edit Scheduled Message"
        backLink="/Messaging/mass-messaging"
        breadCrumbsAboveTitle={[
          { title: 'Messaging', to: '/Messaging' },
          { title: 'Mass Messaging', to: '/Messaging/mass-messaging' },
          { title: 'Edit Message' },
        ]}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      </StandardLayout>
    );
  }

  const audienceSection = (
    <div className="new-mm__section">
      <h3 className="new-mm__section-title">
        <FontAwesomeIcon icon={['far', 'users']} style={{ marginRight: 8 }} />
        AUDIENCE
      </h3>
      <div className="new-mm__audience-row">
        <div className="new-mm__audience-builder">
          <AudienceBuilder
            configuration={audienceConfig}
            onChange={handleAudienceChange}
            audienceCount={audienceCount}
            isCountLoading={isCountLoading}
          />
        </div>
        <div className="new-mm__audience-info">
          <div className="new-mm__audience-count">
            {isCountLoading ? (
              <Spin size="small" />
            ) : (
              <span className="new-mm__audience-count-number">{audienceCount.toLocaleString()}</span>
            )}
            <span className="new-mm__audience-count-label">recipients</span>
          </div>
          {audienceCount > 0 && (
            audienceCount > 5000 ? (
              <Tooltip title="Preview is not available for audiences larger than 5,000 clients">
                <span className="new-mm__preview-link new-mm__preview-link--disabled">Preview Audience</span>
              </Tooltip>
            ) : (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions
              <span className="new-mm__preview-link" onClick={() => setShowPreviewModal(true)}>
                Preview Audience
              </span>
            )
          )}
        </div>
      </div>
    </div>
  );

  return (
    <StandardLayout
      title="Edit Scheduled Message"
      backLink="/Messaging/mass-messaging"
      breadCrumbsAboveTitle={[
        { title: 'Messaging', to: '/Messaging' },
        { title: 'Mass Messaging', to: '/Messaging/mass-messaging' },
        { title: 'Edit Message' },
      ]}
      actions={
        <Button type="primary" onClick={() => formRef.current?.submit()} loading={updateMutation.isLoading}>
          Save Changes
        </Button>
      }
    >
      <MassMessageForm
        ref={formRef}
        audienceCount={audienceCount}
        audienceSection={audienceSection}
        templateButtons={MASS_MESSAGE_TEMPLATE_BUTTONS}
        enableAttachments
        editingMessage={msg}
        onSubmit={handleFormSubmit}
        isSaving={updateMutation.isLoading}
      />

      <AudiencePreviewModal
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        configuration={audienceConfig}
      />
    </StandardLayout>
  );
};

export default EditMassMessage2;
